import React from 'react'
import {LinkedBooleanMappingBlock} from 'src/app/pages/listing/components/LinkedSettingsChildPanel'
import {isTruthySettingValue} from 'src/utils/normalizeBooleanSettingValue'
import type {ChannelSettingSequenceRowSpan} from './channelSettingSequenceLayout'
import {CHANNEL_SETTING_SEQUENCE_SLOT_ALIGN_CONTROL_CLASS} from './channelSettingSequenceLayout'

export type SequenceRenderItem =
  | {
      type: 'full'
      key: string
      rank: number
      content: React.ReactNode
    }
  | {
      type: 'half'
      key: string
      rank: number
      content: React.ReactNode
    }
  | {
      type: 'pair'
      key: string
      rank: number
      left: {key: string; content: React.ReactNode}
      right: {key: string; content: React.ReactNode}
    }

export type SequencePresentationSlot = {
  key: string
  rowSpan: ChannelSettingSequenceRowSpan
  content: React.ReactNode
  isPanel?: boolean
  isHidden?: boolean
}

export type SequenceAlignmentPlan = SequencePresentationSlot & {
  rank: number
  isBooleanHalf?: boolean
  isControlHalf?: boolean
}

export type SequenceRenderPlan = SequencePresentationSlot & {
  rank: number
  slotModifierClass?: string
}

const isBooleanSettingColElement = (node: React.ReactNode): boolean => {
  if (!React.isValidElement(node)) return false
  const className = node.props?.className
  return typeof className === 'string' && className.includes('boolean-setting-col')
}

const isLinkedPanelCol12Element = (node: React.ReactNode): boolean => {
  if (!React.isValidElement(node)) return false
  const className = node.props?.className
  if (typeof className !== 'string') return false
  return className.split(/\s+/).includes('col-12') && !className.includes('col-sm-6')
}

const expandLinkedBooleanMappingBlockSlots = (
  content: React.ReactElement<React.ComponentProps<typeof LinkedBooleanMappingBlock>>,
  baseKey: string,
  defaultRowSpan: ChannelSettingSequenceRowSpan
): SequencePresentationSlot[] | null => {
  if (content.type !== LinkedBooleanMappingBlock) {
    return null
  }

  const {isParentEnabled, sharedToggleAnchorRef} = content.props
  const anchorRef = sharedToggleAnchorRef

  const toggleNode = React.cloneElement(content, {
    sequencePresentationPart: 'toggle' as const,
    sharedToggleAnchorRef: anchorRef,
  })

  if (!isParentEnabled) {
    return [{key: baseKey, rowSpan: 'half', content: toggleNode}]
  }

  const panelNode = React.cloneElement(content, {
    sequencePresentationPart: 'panel' as const,
    sharedToggleAnchorRef: anchorRef,
  })

  return [
    {key: `${baseKey}-toggle`, rowSpan: 'half', content: toggleNode},
    {key: `${baseKey}-panel`, rowSpan: 'full', content: panelNode},
  ]
}

/**
 * Splits boolean toggle + linked panel fragments into two grid slots (half + full)
 * so they match staging's two direct `.row > .col-*` children.
 */
export const expandSequencePresentationSlots = (
  content: React.ReactNode,
  baseKey: string,
  defaultRowSpan: ChannelSettingSequenceRowSpan = 'half',
  options?: {
    linkedMappingAnchorRef?: React.Ref<HTMLDivElement>
    linkedParentToggleRef?: React.RefObject<HTMLDivElement>
    linkedChildPanelRef?: React.RefObject<HTMLDivElement>
    retainNestedPanel?: boolean
  }
): SequencePresentationSlot[] => {
  if (React.isValidElement(content) && content.type === LinkedBooleanMappingBlock) {
    const withRef =
      options?.linkedMappingAnchorRef && !content.props.sharedToggleAnchorRef
        ? React.cloneElement(content, {
            sharedToggleAnchorRef: options.linkedMappingAnchorRef,
          })
        : content
    const mappingSlots = expandLinkedBooleanMappingBlockSlots(
      withRef as React.ReactElement<React.ComponentProps<typeof LinkedBooleanMappingBlock>>,
      baseKey,
      defaultRowSpan
    )
    if (mappingSlots) {
      return mappingSlots
    }
  }

  if (React.isValidElement(content)) {
    const childItem = content.props?.childItem
    const settingField = content.props?.settingField
    const values = content.props?.values

    const isDynamicBooleanChild =
      childItem?.input_type === 'BOOLEAN' &&
      typeof childItem?.name === 'string' &&
      'settingField' in content.props &&
      'values' in content.props

    if (isDynamicBooleanChild) {
      const settingValue = settingField
        ? values?.[`${settingField}_setting`]?.[childItem.name]
        : values?.[childItem.name]

      const isEnabled = isTruthySettingValue(settingValue)
      const hasNestedContent =
        Boolean(childItem?.children?.length) ||
        Boolean(childItem?.field_mappings?.length)

      if (hasNestedContent) {
        const sharedParentToggleRef =
          options?.linkedParentToggleRef ?? React.createRef<HTMLDivElement>()
        const sharedChildPanelRef =
          options?.linkedChildPanelRef ?? React.createRef<HTMLDivElement>()

        const toggleNode = React.cloneElement(content, {
          sequencePresentationPart: 'toggle' as const,
          sequenceLinkedParentToggleRef: sharedParentToggleRef,
          sequenceLinkedChildPanelRef: sharedChildPanelRef,
        })

        if (!isEnabled && !options?.retainNestedPanel) {
          return [
            {
              key: `${baseKey}-toggle`,
              rowSpan: 'half',
              content: toggleNode,
            },
          ]
        }

        const panelNode = React.cloneElement(content, {
          sequencePresentationPart: 'panel' as const,
          sequenceLinkedParentToggleRef: sharedParentToggleRef,
          sequenceLinkedChildPanelRef: sharedChildPanelRef,
        })

        return [
          {
            key: `${baseKey}-toggle`,
            rowSpan: 'half',
            content: toggleNode,
          },
          {
            key: `${baseKey}-panel`,
            rowSpan: 'full',
            content: panelNode,
            isPanel: true,
            isHidden: !isEnabled,
          },
        ]
      }
    }
  }

  if (!React.isValidElement(content) || content.type !== React.Fragment) {
    return [{key: baseKey, rowSpan: defaultRowSpan, content}]
  }

  const fragmentChildren = React.Children.toArray(content.props.children)
  if (fragmentChildren.length !== 2) {
    return [{key: baseKey, rowSpan: defaultRowSpan, content}]
  }

  const [first, second] = fragmentChildren
  if (!isBooleanSettingColElement(first) || !isLinkedPanelCol12Element(second)) {
    return [{key: baseKey, rowSpan: defaultRowSpan, content}]
  }

  return [
    {key: `${baseKey}-toggle`, rowSpan: 'half', content: first},
    {key: `${baseKey}-panel`, rowSpan: 'full', content: second},
  ]
}

/**
 * Marks boolean half-slots that share a 2-col row with a text/control field so
 * only those toggles get control-row alignment — toggle|toggle pairs stay unchanged.
 */
export const markSequenceHalfRowAlignment = (
  plans: SequenceAlignmentPlan[]
): SequenceRenderPlan[] => {
  const indexed = plans.map((plan, index) => ({...plan, index}))
  const sorted = [...indexed].sort((a, b) => a.rank - b.rank)
  const modifiers = new Map<number, string>()

  let halfBuffer: typeof indexed = []

  const flushHalfBuffer = () => {
    if (halfBuffer.length === 2) {
      const [left, right] = halfBuffer
      if (left.isBooleanHalf && right.isControlHalf) {
        modifiers.set(left.index, CHANNEL_SETTING_SEQUENCE_SLOT_ALIGN_CONTROL_CLASS)
      } else if (right.isBooleanHalf && left.isControlHalf) {
        modifiers.set(right.index, CHANNEL_SETTING_SEQUENCE_SLOT_ALIGN_CONTROL_CLASS)
      }
    }
    halfBuffer = []
  }

  for (const plan of sorted) {
    if (plan.rowSpan === 'full') {
      flushHalfBuffer()
      continue
    }

    halfBuffer.push(plan)
    if (halfBuffer.length === 2) {
      flushHalfBuffer()
    }
  }

  flushHalfBuffer()

  return plans.map((plan, index) => ({
    key: plan.key,
    rank: plan.rank,
    rowSpan: plan.rowSpan,
    content: plan.content,
    slotModifierClass: modifiers.get(index),
  }))
}

/**
 * Groups rank-sorted half-slots into pair rows (independent columns) or singles.
 * Presentation only — does not change rank values or registry ordering.
 */
export const buildSequenceRenderItems = (
  plans: SequenceAlignmentPlan[]
): SequenceRenderItem[] => {
  const sorted = [...plans].sort((a, b) => a.rank - b.rank)
  const items: SequenceRenderItem[] = []
  let halfBuffer: SequenceAlignmentPlan[] = []
  let pendingPanels: SequenceAlignmentPlan[] = []

  const flushPanels = () => {
    pendingPanels.forEach((plan) => {
      items.push({
        type: 'full',
        key: plan.key,
        rank: plan.rank,
        content: plan.content,
      })
    })
    pendingPanels = []
  }

  const flushHalfBuffer = () => {
    if (halfBuffer.length === 2) {
      const [leftPlan, rightPlan] = halfBuffer

      items.push({
        type: 'pair',
        key: `pair:${leftPlan.key}:${rightPlan.key}`,
        rank: Math.min(leftPlan.rank, rightPlan.rank),
        left: {key: leftPlan.key, content: leftPlan.content},
        right: {key: rightPlan.key, content: rightPlan.content},
      })
    } else if (halfBuffer.length === 1) {
      items.push({
        type: 'half',
        key: halfBuffer[0].key,
        rank: halfBuffer[0].rank,
        content: halfBuffer[0].content,
      })
    }
    halfBuffer = []
    flushPanels()
  }

  for (const plan of sorted) {
    if (plan.isPanel) {
      pendingPanels.push(plan)
      continue
    }

    if (plan.rowSpan === 'full') {
      flushHalfBuffer()
      items.push({
        type: 'full',
        key: plan.key,
        rank: plan.rank,
        content: plan.content,
      })
      continue
    }

    halfBuffer.push(plan)
    if (halfBuffer.length === 2) {
      flushHalfBuffer()
    }
  }

  flushHalfBuffer()
  flushPanels()
  return items
}
